import { useEffect, useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FieldErrors, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { QueueRule } from '@rossum/api-client/schemas';
import { RuleTemplate } from '../hooks/useSuggestRule';
import { extractKnownActions, ruleFormSchema, ValidRuleForm } from '../utils';

export const EMPTY_RULE_DEFAULTS: ValidRuleForm = {
  name: '',
  description: '',
  triggerCondition: '',
  enabled: true,
  actions: [],
};

export const useRuleForm = ({
  rule,
  errors,
  stateRule,
  disabled = false,
}: {
  rule?: QueueRule | RuleTemplate;
  errors?: FieldErrors<ValidRuleForm>;
  stateRule?: Partial<ValidRuleForm>;
  disabled?: boolean;
}) => {
  const intl = useIntl();

  const form = useForm<ValidRuleForm>({
    criteriaMode: 'all',
    defaultValues: rule ? extractKnownActions(rule) : EMPTY_RULE_DEFAULTS,
    errors,
    resolver: zodResolver(ruleFormSchema(intl)),
    mode: 'onSubmit',
    disabled,
  });

  // Apply stateRule values once on mount, keeping defaults for dirty comparison
  const stateRuleAppliedRef = useRef(false);
  useEffect(() => {
    if (!rule && stateRule && !stateRuleAppliedRef.current) {
      stateRuleAppliedRef.current = true;
      form.reset(
        { ...EMPTY_RULE_DEFAULTS, ...stateRule },
        { keepDefaultValues: true }
      );
    }
  }, [form, rule, stateRule]);

  return form;
};
